import {
	type BondRequestBondType,
	ContractBondSubtype,
	ContractSuretyType,
	Dtos,
} from "@inrev/common";
import type { Path } from "react-hook-form";
import { z } from "zod";
import { getSchemaDefaults } from "../../../../utils/form";
import { commercialBondFormSchema } from "../../../shared/bond/commercialBondSchema";

export { transformBondFormForSubmission } from "../../../shared/bond/bondUtils";

const QuoteSchemas = Dtos.SuretyQuote.Create;
const {
	minimalBidQuoteFieldOverrides,
	minimalFinalQuoteFieldOverrides,
	minimalBidToFinalQuoteFieldOverrides,
} = QuoteSchemas;

const minimalBidQuoteCreateSchema = z.object({
	...QuoteSchemas.bidQuoteCreateSchema.shape,
	...minimalBidQuoteFieldOverrides,
});

const minimalFinalQuoteCreateSchema = z.object({
	...QuoteSchemas.finalQuoteCreateSchema.shape,
	...minimalFinalQuoteFieldOverrides,
});

const minimalBidToFinalQuoteCreateSchema = z.object({
	...QuoteSchemas.bidToFinalCreateSchema.shape,
	...minimalBidToFinalQuoteFieldOverrides,
});

const minimalQuoteSchema = z.discriminatedUnion("contractSuretyType", [
	minimalBidQuoteCreateSchema,
	minimalFinalQuoteCreateSchema,
	minimalBidToFinalQuoteCreateSchema,
]);

// Legacy (non-minimal) mode collects only project.description, never project.name.
const requiredDescriptionProjectSchema = z.object({
	...QuoteSchemas.projectSchema.shape,
	// nullish input keeps form defaults compatible; pipe output is hard-required for asterisk derivation.
	description: z.string().nullish().pipe(z.string().trim().min(1, "Required")),
});

const fullQuoteSchema = z.discriminatedUnion("contractSuretyType", [
	z.object({
		...QuoteSchemas.bidQuoteCreateSchema.shape,
		project: requiredDescriptionProjectSchema,
	}),
	z.object({
		...QuoteSchemas.finalQuoteCreateSchema.shape,
		project: requiredDescriptionProjectSchema,
	}),
	QuoteSchemas.bidToFinalCreateSchema,
]);

export const getBondRequestFormValidationSchema = ({ minimalMode }: { minimalMode: boolean }) =>
	z.object({
		quote: minimalMode ? minimalQuoteSchema : fullQuoteSchema,
		contractAmountEdited: z.boolean().optional(),
	});

export const bondRequestFormValidationSchema = getBondRequestFormValidationSchema({
	minimalMode: false,
});

export type BondRequestCreateFormData = z.input<typeof bondRequestFormValidationSchema>;

export type BondRequestFormValues = {
	quote: z.infer<typeof QuoteSchemas.Request.quoteSchema>;
	bondType?: BondRequestBondType;
	contractBondType?: ContractBondSubtype;
	commercialBond?: z.infer<typeof commercialBondFormSchema>;
	contractAmountEdited?: boolean;
};

export const fp = (path: Path<BondRequestFormValues>): string => path;

export const getDefaultBondRequestFormData = (
	contractSuretyType?: ContractSuretyType,
	overrides?: Record<string, unknown>,
): BondRequestCreateFormData =>
	({
		quote: {
			// Merge the quote union so an optional bid-to-final project does not wipe the bid/final project object.
			...getSchemaDefaults(QuoteSchemas.Request.quoteSchema),
			contractSuretyType,
			suretyType: "contract",
			...overrides,
		},
	}) as BondRequestCreateFormData;
